import { AllSelection } from 'prosemirror-state';
import { serializer } from './wikitext/index';
import { history } from 'prosemirror-history';

class ProseMirrorEditorHandle {
   constructor(editor, container, enterManager) {
      this.editor = editor;
      this.container = container;
      this.enterManager = enterManager;
      this.originalState = editor.state;

      /**
       * Disable the enter key in this editor. This allows the text
       * completer to keep the editor from eating the enter it uses for
       * submission. (To be precise: the editor won't eat the event, but
       * it will add a `<p>` before the name completer gets to do its
       * thing, which breaks the completer.)
       */
      this.disableEnter = enterManager.disable.bind(enterManager);
      /**
       * Reenable the enter key in this editor. See `disableEnter` for
       * reasons.
       */
      this.enableEnter = enterManager.enable.bind(enterManager);
   }

   /**
    * Returns the wikitext version of the contents of this
    * ProseMirror editor.
    */
   get value() {
      return serializer.serialize(this.editor.state.doc);
   }

   /**
    * Returns the plaintext version of the contents of this
    * ProseMirror editor
    */
   get text() {
      return this.editor.state.doc.textContent;
   }

   get el() {
      return this.container;
   }

   get editable() {
      return this.editor.dom;
   }

   /**
    * Clear all text in this editor and reset undo/redo history.
    */
   clear() {
      const doc = this.editor.state.doc;
      const all = new AllSelection(doc);
      const transaction = this.editor.state.tr.setSelection(all).deleteSelection();
      this.editor.dispatch(transaction);

      const resetHistoryState = resetHistory(this.editor.state);
      this.editor.updateState(resetHistoryState);
   }

   focus() {
      this.editor.focus();
   }

   /**
    * Reset the prosemirror state to the original document contents.
    */
   reset() {
      this.editor.updateState(this.originalState);
   }

   /**
    * Overwrite the originalState with the current state.
    *
    * This is used on a successful comment edit submission so that cancelling a
    * further edit reverts to the state at the time of the most recent save.
    */
   saveState() {
      this.originalState = this.editor.state;
   }
}

/**
 * Consumes an EditorState, removes the History plugin and returns a new
 * EditorState with a newly initialized History plugin.
 */
function resetHistory(state) {
   const plugins = state.plugins.filter(plugin => !/^history/.test(plugin.key));
   const removedHistoryState = state.reconfigure({ plugins });

   plugins.push(history());
   return removedHistoryState.reconfigure({ plugins });
}

export { ProseMirrorEditorHandle };
